import { get } from 'lodash';
import { INPUT_AUTOCOMPLETE, INPUT_TYPE, MARKET } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { useCallback, useState } from 'react';
import { useRootStore } from '@nandosaus/state-management';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';

import { A, P } from '../typography';
import { Box, Flex } from '../grid';
import { Form } from '../form';
import { FormikTextareaInput, FormikTextInput } from '../input';
import { getApiClient } from '../../../lib/api-client';
import { LoadingIndicator } from '../icons/loading-indicator';
import { useMarket } from '../../hooks/use-market';
import { useSchema } from '../../hooks/use-schema';

const SUBMIT_NPS_COMPETITION_FORM_MUTATION = gql`
  mutation submitNpsCompetitionForm($input: NpsCompetitionInput!) {
    submitNpsCompetitionForm(input: $input)
  }
`;

const CompetitionForm = observer(({ setSuccess }) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const validationSchema = useSchema('npsCompetitionForm');

  const onSubmit = useCallback(async (values, { setSubmitting }) => {
    try {
      const client = await getApiClient();
      await client.mutate({
        mutation: SUBMIT_NPS_COMPETITION_FORM_MUTATION,
        variables: { input: values },
      });
      setSuccess(true);
    } catch (error) {
      const message = get(error, 'errors[0].message', error.message);
      setErrorMessage(message);
    }

    setSubmitting(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { MemberStore } = useRootStore();

  if (MemberStore.loading)
    return (
      <Flex justifyContent="flex" mt={4}>
        <LoadingIndicator />
      </Flex>
    );

  const { givenName, familyName, email, mobile } = MemberStore.profile || {};

  return (
    <Form
      error={errorMessage}
      prefilled={!!MemberStore.profile}
      heading="Win a $100 Nando’s Voucher!*"
      initialValues={{
        givenName: givenName || '',
        familyName: familyName || '',
        email: email || '',
        mobile: mobile || '',
        response: '',
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      buttonText="Enter for a chance to win $100"
      paragraph="You could be the next winner of this epic voucher. Simply pop in your details below for a chance to win a $100 Nando's Voucher."
      bottomElement={
        <P variant={4} mt={1}>
          * Minimum entry and eligibility criteria apply. By submitting this form, you agree to our Terms and
          Conditions.{' '}
          <A
            variant={4}
            target="_blank"
            href={useMarket() === MARKET.AUSTRALIA ? '/rate-your-nandos' : '/t-c-rate-your-nandos'}
          >
            Click here
          </A>{' '}
          for full competition and prize T&C’s.
        </P>
      }
    >
      <>
        <Box mb={1}>
          <FormikTextInput name="givenName" label="First Name" type={INPUT_TYPE.TEXT} />
        </Box>
        <Box mb={1}>
          <FormikTextInput name="familyName" label="Last Name" type={INPUT_TYPE.TEXT} />
        </Box>
        <Box mb={1}>
          <FormikTextInput name="email" label="Email" type={INPUT_TYPE.EMAIL} autoComplete={INPUT_AUTOCOMPLETE.EMAIL} />
        </Box>
        <Box mb={1}>
          <FormikTextInput
            name="mobile"
            label="Mobile"
            type={INPUT_TYPE.PHONE}
            autoComplete={INPUT_AUTOCOMPLETE.PHONE}
          />
        </Box>
        <Box mb={1}>
          <P fontWeight="bold">Tell us in 25 words or less what you love most about Nando’s</P>
          <FormikTextareaInput name="response" rows={4} />
        </Box>
      </>
    </Form>
  );
});

Form.propTypes = {
  setSuccess: PropTypes.func.isRequired,
};

export { CompetitionForm };
